import {
  type ComponentPropsWithoutRef,
  createElement,
  forwardRef,
  type CSSProperties,
  type Ref,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import classNames from 'classnames'
import { Tooltip, type ITooltipProps } from 'shared/ui/Tooltip'
import { getAriaLabel } from 'shared/lib'
import styles from './styles.module.scss'

const variant = [
  'text-input-helper',
  'body-xs-regular',
  'body-xs-medium',
  'body-xl-medium',
  'body-xxl-medium',
  'body-xxs-medium',
  'body-sm-regular',
  'body-sm-medium',
  'body-rg-regular',
  'body-rg-medium',
  'body-rg-semibold',
  'body-lg-medium',
  'body-lg-regular',
  'body-lg-semibold',
  'body-md-regular',
  'body-md-medium',
  'body-md-semibold',
  'body-xxl-semibold',
  'heading-sm',
  'heading-md',
  'heading-lg',
  'text-input-label-small',
  'text-input-field',
  'text-input-field-xl',
  'button-small',
  'button-medium',
  'body-small-medium',
  'text-input-field-r',
  'body-xl-semibold',
] as const

export type TextStylesType = (typeof variant)[number]

export type ITypographyProps = ComponentPropsWithoutRef<'span'> & {
  className?: string
  variant?: TextStylesType
  tag?: 'span' | 'div' | 'a'
  ellipsis?: boolean
  onShowEllipsis?: (status: boolean) => void
  link?: boolean
  linkLight?: boolean
  color?: 'tertiary' | 'negativePrimary' | string
  pointer?: boolean
  marginBottom?: CSSProperties['marginBottom']
  marginTop?: CSSProperties['marginBottom']
  textDecoration?: string
  tooltipProps?: ITooltipProps
  width?: CSSProperties['width']
  ariaLabel: string
  dataHighlightId?: string
}

export const Typography = forwardRef(function TypographyForwardRef(
  {
    className,
    variant = 'body-rg-regular',
    tag = 'span',
    ellipsis,
    onShowEllipsis,
    link,
    linkLight,
    color,
    pointer,
    marginBottom,
    marginTop,
    tooltipProps,
    width,
    ariaLabel,
    dataHighlightId,
    ...props
  }: ITypographyProps,
  forwardedRef: Ref<HTMLElement>
) {
  const objColor: { [key: string]: string } = {
    tertiary: 'var(--content-primary-tertiary)',
    negativePrimary: 'var(--content-negative-primary)',
  }

  const internalRef = useRef<HTMLElement | null>(null)

  const setRootRef = useCallback(
    (node: HTMLElement | null) => {
      internalRef.current = node

      if (typeof forwardedRef === 'function') {
        forwardedRef(node)
      } else if (forwardedRef !== null) {
        Reflect.set(forwardedRef, 'current', node)
      }
    },
    [forwardedRef]
  )

  const [showTooltip, setShowTooltip] = useState(false)
  const [elementWidth, setElementWidth] = useState<number | null>(null)
  const handleResize = useCallback(() => {
    const element = internalRef.current
    if (!element) return

    const show = element.scrollWidth > element.clientWidth

    if (onShowEllipsis) {
      onShowEllipsis(show)
    } else {
      setShowTooltip(show)
    }

    if (show) {
      setElementWidth(element.clientWidth)
    } else {
      setElementWidth(null)
    }
  }, [onShowEllipsis])

  useEffect(() => {
    if (!tooltipProps && !onShowEllipsis) return

    handleResize()
    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [tooltipProps, onShowEllipsis, handleResize])

  ariaLabel = `Typography_${ariaLabel}`

  const Component = createElement(tag, {
    ...props,
    style: {
      color: color ? objColor[color] || color : undefined,
      ...(pointer ? { pointer: 'pointer' } : null),
      marginBottom,
      marginTop,
      width: elementWidth || width,
    },
    ref: setRootRef,
    className: classNames(
      styles.wrap,
      styles[variant],
      ellipsis && styles.ellipsis,
      link && styles.link,
      linkLight && styles.linkLight,
      className
    ),
    ...(ariaLabel
      ? {
          'aria-label': getAriaLabel(ariaLabel),
        }
      : null),
    ...(dataHighlightId
      ? {
          'data-highlight-id': dataHighlightId,
        }
      : null),
  })

  if (tooltipProps) {
    return (
      <Tooltip
        placement='top'
        disableInteractive
        {...tooltipProps}
        label={showTooltip ? tooltipProps.label : null}
      >
        {Component}
      </Tooltip>
    )
  }

  return Component
})

Typography.displayName = 'Typography'
